import { useDeferredScript } from '@bluheadless/ui-logic/src/hooks/use-script/useDeferredScript'
import { SCRIPT_STATUS_LOADING } from '@bluheadless/ui-logic/src/hooks/use-script/useScript'
import { useConfig } from '@bluheadless/ui-logic/src/providers/config'
import { useDetectBot } from '@bluheadless/ui-logic/src/hooks/useDetectBot'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import LoadingIcon from '../../atoms/icons/loading-icon'
import SupportIcon from '../../atoms/icons/support-icon'
import FloatingAction from '../../molecules/floating-action'

const ZendeskButton = ({ backgroundColor, position, ...props }) => {
	const {
		locale,
		zendesk: { scriptSrc, departments, preselectedDepartment },
	} = useConfig()

	const { isBot, loading } = useDetectBot()

	const { status, load: loadScript } = useDeferredScript(
		useMemo(
			() => ({
				src: !loading && !isBot ? scriptSrc : null,
				attributes: {
					id: 'ze-snippet',
					defer: '',
					fetchpriority: 'low',
				},
			}),
			[isBot, loading, scriptSrc]
		)
	)

	const onClose = useRef()
	const onOpen = useRef()

	useEffect(() => {
		if (status === 'ready') {
			window.zE('webWidget', 'hide')
			window.zESettings = {
				webWidget: {
					position: { horizontal: position },
					color: {
						theme: backgroundColor,
						launcher: backgroundColor,
					},
					chat: {
						departments: {
							enabled: departments?.map((department) => department.code),
							select: preselectedDepartment,
						},
					},
				},
			}

			window.zE('webWidget:on', 'close', function () {
				window.zE('webWidget', 'hide')
				if (onClose.current) {
					onClose.current()
				}
			})
		}
	}, [status, backgroundColor, position, departments, preselectedDepartment])

	const openWidget = useCallback(async () => {
		const ready = await loadScript() // start script and wait it to be downloaded parsed and executed
		if (ready) {
			window.zE('webWidget', 'show')
			window.zE('webWidget', 'open')
			window.zE('webWidget', 'setLocale', locale.split('-')[0])

			if (onOpen.current) {
				onOpen.current()
			}
		}
	}, [locale, loadScript])

	return (
		<FloatingAction
			onCloseRef={onClose}
			onOpenRef={onOpen}
			onClickCapture={openWidget}
			Icon={status === SCRIPT_STATUS_LOADING ? LoadingIcon : SupportIcon}
			backgroundColor={backgroundColor}
			position={position}
			{...props}
		/>
	)
}

ZendeskButton.defaultProps = {
	textColor: '#FFFFFF',
	backgroundColor: '#141616',
	position: 'left',
}

ZendeskButton.propTypes = {
	...FloatingAction.propTypes,
}

export default ZendeskButton
